import { useEffect, useState } from "react";
import { allMenuItems } from "../../../utils/menuData";
import Filters from "../../Filters";
import { motion } from "framer-motion";
import { MdShoppingCart } from "react-icons/md";
import { getToastTabUrl, TOAST_TAB_BASE_URL } from "../../../utils/toastTabLinks";

const Menu = ({ title }: { title?: string }) => {
  const [filter, setFilter] = useState<string>("all");
  const [filteredItems, setFilteredItems] = useState(allMenuItems);

  useEffect(() => {
    if (filter === "all") {
      setFilteredItems(allMenuItems);
    } else {
      const filtered = allMenuItems.filter(item => item.category === filter);
      setFilteredItems(filtered);
    }
  }, [filter]);

  const handleOrderClick = (itemTitle: string) => {
    const toastTabUrl = getToastTabUrl(itemTitle);
    if (toastTabUrl) {
      window.open(toastTabUrl, '_blank');
    } else {
      // Fallback to main ordering page if specific item URL not found
      window.open(TOAST_TAB_BASE_URL, '_blank');
    }
  };

  return (
    <section className="w-full py-16 px-4 bg-gradient-to-br from-gray-50 via-white to-[#fef9e7]/30 flex justify-center" id="menu">
      <div className="w-full max-w-7xl">
        <div className="text-center mb-4">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#ac9430] to-[#227a32] bg-clip-text text-transparent mb-4">
            {title || "Our Complete Menu"}
          </h2>
          <p className="text-gray-700 text-lg mb-6">
            Authentic South Indian & Indo-Chinese Cuisine
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#947d25] to-[#ac9430] mx-auto rounded-full"></div>
        </div>
        
        <Filters filter={filter} setFilter={setFilter} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full"
        >
          <div className="max-h-[700px] overflow-y-auto scrollbar-thin scrollbar-thumb-[#ac9430] scrollbar-track-gray-200 pr-2">
            {filteredItems && filteredItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(172, 148, 48, 0.3)" }}
                    className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group"
                  >
                    {/* Decorative gradient accent */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ac9430] via-[#227a32] to-[#ac9430]" />
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-800 group-hover:text-[#ac9430] transition-colors flex-1 pr-2 leading-tight">
                          {item.title}
                        </h3>
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleOrderClick(item.title)}
                          className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#ac9430] to-[#227a32] flex items-center justify-center shadow-lg hover:shadow-xl transition-all"
                          title="Order now on Menufy"
                        >
                          <MdShoppingCart className="text-white text-lg" />
                        </motion.button>
                      </div>
                      
                      <p className="text-sm text-gray-600 leading-relaxed mb-4 min-h-[40px]">
                        {item.description}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="px-4 py-2 rounded-full bg-gradient-to-r from-[#ac9430] to-[#947d25] shadow-md">
                            <p className="text-lg font-bold text-white">
                              ${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}
                            </p>
                          </div>
                        </div>
                        
                        {item.calories && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full">
                            <span className="font-medium">{item.calories}</span>
                            <span>cal</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Hover effect bottom accent */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#ac9430] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-2xl text-gray-400">No items found in this category</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Menu;
